import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { uploadAndGetSignedUrl } from "@/lib/storage";

interface VoiceRecorderProps {
  workspaceId: string;
  onRecorded: (url: string) => void;
}

export default function VoiceRecorder({ workspaceId, onRecorded }: VoiceRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunks.current = [];
      recorder.ondataavailable = (e) => chunks.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        setUploading(true);
        const path = `${workspaceId}/voice-${Date.now()}.webm`;
        const { signedUrl, error: upErr } = await uploadAndGetSignedUrl("couple-uploads", path, blob);
        if (upErr || !signedUrl) {
          toast.error("Upload failed", { description: upErr || "Unknown error" });
          setUploading(false);
          return;
        }
        onRecorded(path);
        setUploading(false);
      };
      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    mediaRecorder.current?.stop();
    setRecording(false);
  };

  if (uploading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
      </div>
    );
  }

  return recording ? (
    <button
      onClick={stop}
      className="flex items-center gap-2 px-3 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-medium"
    >
      <Square className="w-3.5 h-3.5" /> Stop Recording
    </button>
  ) : (
    <button
      onClick={start}
      className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors"
    >
      <Mic className="w-4 h-4" /> Record Voice Note
    </button>
  );
}
